import { Button, Flex, type IChatMessage } from '@kognitos/lattice';

import { ChatIntegrationConnection } from '@/modules/automation/components/QuillChat/components/ChatIntegrationConnection';
import i18n from '@/shared/i18n';

import type {
  IRainToolCallState,
  IRainToolRenderContext,
} from '../../hooks/types';

/**
 * Declared, not assigned to a const: `ChatIntegrationConnection` pulls in the
 * automation module, which reaches back into the Rain adapters and through them
 * to the agent profiles that import this file. That cycle reads this binding
 * while the module is still evaluating, and only a hoisted declaration is
 * initialized by then — an arrow const leaves `undefined` in four of the five
 * profiles' renderer maps.
 *
 * The connection picker: shows the connections that exist for a book, lets the
 * user pick one or create a new one, and resolves the tool call with the chosen
 * id. `onUnavailable` fails the call so the agent can take another route, and
 * the skip pill closes the request without one.
 *
 * Shared by every agent that asks for a connection. smith reaches it under its
 * own `add_integration`, which takes the same `appName` / `appVersion` /
 * `reason` arguments.
 */
export function renderConnectionPickerCard(
  state: IRainToolCallState,
  ctx: IRainToolRenderContext,
): IChatMessage {
  const appName =
    typeof state.args.appName === 'string' ? state.args.appName : '';
  const appVersion =
    typeof state.args.appVersion === 'string' ? state.args.appVersion : '';
  const reason =
    typeof state.args.reason === 'string' ? state.args.reason : undefined;
  // `appVersion` is optional: the model rarely knows a book's real semver
  // (sap's latest is 2.6.2), so the card only waits on the app name and lets
  // the widget resolve the version. Without a name there's nothing to render.
  if (appName === '') {
    return ctx.renderCustomCard(state, null);
  }
  const isPending = state.status === 'running';
  // On history replay the client-side `resolvedConnectionId` is gone, but a
  // successful call carries the chosen connection id as its tool result.
  const connectionId =
    state.resolvedConnectionId ??
    (state.status === 'success' && typeof state.result === 'string'
      ? state.result
      : undefined);
  return ctx.renderCustomCard(
    state,
    <Flex direction="column" gap={2} align="start">
      <ChatIntegrationConnection
        integration_name={appName}
        integration_version={appVersion === '' ? undefined : appVersion}
        connectionId={connectionId}
        reason={reason}
        requestId={state.id}
        // Anything that isn't pending or connected ended without a
        // connection — the user skipped it, or Rain cancelled the call.
        isDeclined={!isPending && connectionId === undefined}
        onSelectConnection={c =>
          ctx.resolvers.resolveRequestConnection(state.id, c.connection_id)
        }
        onUnavailable={() => ctx.resolvers.failRequestConnection(state.id)}
      />
      {/* Closing the request is a reply, not a dismissal: the pill sends a
          real user message and Rain's redirect close-out cancels the
          suspended call. Styled like the follow-up suggestion pills so it
          reads as a suggested answer to the agent's ask. */}
      {isPending ? (
        <Button
          variant="outline"
          size="sm"
          disabled={state.isSubmitting}
          onClick={() => void ctx.resolvers.skipRequestConnection(state.id)}
          data-testid="rain-skip-connection"
        >
          {i18n.t('rain.requestConnection.skip', {
            ns: 'rain',
            app: appName,
          })}
        </Button>
      ) : null}
    </Flex>,
  );
}
